import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { format } from "date-fns";

const AcknowledgeAssetPolicyModal = ({
  show,
  onClose,
  onSubmit,
  pdfUrl,
  assignedDate,
  viewPolicy = false,
}) => {
  const [step, setStep] = useState(1);
  const [assetReceived, setAssetReceived] = useState(null);
  const [policyAcknowledged, setPolicyAcknowledged] = useState(null);
  const [policyRead, setPolicyRead] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const [receivedDate, setReceivedDate] = useState("");
  const [error, setError] = useState({});

  useEffect(() => {
    if (viewPolicy) {
      setStep(2);
    }
  }, [viewPolicy]);

  useEffect(() => {
    let timer;
    if (timerStarted && !policyRead) {
      timer = setTimeout(() => {
        setPolicyRead(true);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [timerStarted, policyRead]);

  useEffect(() => {
    if (receivedDate && assetReceived) {
      setStep(2);
      setTimerStarted(true);
    }
  }, [receivedDate, assetReceived]);

  const handlePolicyAcknowledgedChange = (e) => {
    const value = e.target.checked;
    setPolicyAcknowledged(value);
  };

  const formatDateForSQL = (date) => {
    return format(date, "yyyy-MM-dd");
  };

  const handleFormSubmit = () => {
    if (assetReceived && policyAcknowledged) {
      const data = {
        employee_received_date: formatDateForSQL(receivedDate),
        policy_acknowledged: policyAcknowledged,
        employee_received: assetReceived,
      };
      onSubmit(data);
    } else onClose();
  };

  const handleDateChange = (e) => {
    setReceivedDate(e.target.value);
    if (e.target.value) {
      const assignedDateObj = new Date(assignedDate);
      const receivedDateObj = new Date(e.target.value);
      if (receivedDateObj < assignedDateObj) {
        setError({
          receivedDate: "Received date cannot be earlier than assigned date.",
        });
      } else {
        setError((prev) => ({ ...prev, receivedDate: "" }));
      }
    }
  };

  const handleAssetReceive = () => {
    setAssetReceived(true);
    setStep(2);
    setTimerStarted(true);
  };

  return (
    <Modal
      show={show}
      size={(assetReceived && receivedDate) || viewPolicy ? "lg" : "sm"}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>Asset Policy {viewPolicy ? "" : "Agreement"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 1 && (
          <Form>
            <Form.Group>
              <Form.Label>Asset Assigned Date: </Form.Label>
              <Form.Label className="ms-2 fs-6">
                {formatDateForSQL(new Date(assignedDate))}
              </Form.Label>
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Asset Received Date</Form.Label>
              <Form.Control
                type="date"
                value={receivedDate}
                min={formatDateForSQL(new Date(assignedDate))}
                onChange={handleDateChange}
              />
              {error.receivedDate && (
                <small style={{ color: "red" }}>* {error.receivedDate}</small>
              )}
            </Form.Group>
          </Form>
        )}

        {step === 2 && (
          <>
            <div
              style={{
                height: "400px",
                border: "1px solid #ddd",
              }}
            >
              <Worker
                workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}
              >
                <Viewer
                  fileUrl={pdfUrl}
                  theme={{
                    style: {
                      pageWrapper: {
                        display: "block",
                      },
                    },
                  }}
                />
              </Worker>
            </div>
            {!viewPolicy && (
              <>
                {!policyRead && (
                  <p style={{ color: "red", marginTop: "10px" }}>
                    * Please read the document. The acknowledgment option will
                    be available in 5 seconds.
                  </p>
                )}
                {policyRead && (
                  <Form className="m-4">
                    <Form.Group>
                      <Form.Check
                        type="checkbox"
                        checked={policyAcknowledged}
                        label="Yes, I have read the policy and acknowledge it."
                        name="policyAcknowledged"
                        onChange={handlePolicyAcknowledgedChange}
                      />
                    </Form.Group>
                  </Form>
                )}
              </>
            )}
          </>
        )}

        {step === 3 && (
          <p>
            {assetReceived === false
              ? "You have indicated that you have not received the asset."
              : "Thank you for reviewing the policy."}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {step === 2 && !viewPolicy && (
          <Button
            variant="primary"
            onClick={handleFormSubmit}
            disabled={
              (step === 2 && (!policyRead || policyAcknowledged === null)) ||
              (step === 1 && assetReceived === null)
            }
          >
            Submit
          </Button>
        )}
        {step === 1 && (
          <Button
            variant="primary"
            onClick={handleAssetReceive}
            disabled={error.receivedDate !== "" || !receivedDate}
          >
            Next
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AcknowledgeAssetPolicyModal;
